/**
 * A pre-authored exploration, so the tool is legible before anyone spends a
 * token — and so the reference bar for what a *good* node looks like is set by
 * something real rather than lorem ipsum.
 *
 * The content is deliberately honest: the confident claims are ones that hold
 * up, the contested ones are marked contested, and three of the five top-level
 * branches end in well-argued dead ends. That is what a real exploration of
 * this idea looks like, and a tool that pretended otherwise would be useless.
 *
 * Reserve nodes hang off specific parents keyed to a specific operation. They
 * unfurl with the same choreography as live model output, so the interaction
 * is genuinely exercised before a key is added.
 */

import type {
  Lens,
  NodeKind,
  Op,
  Outcome,
  PanelVerdict,
  ReasonNode,
  RunVerb,
  Session,
} from '../core/types'
import { OP_LABEL } from '../core/types'
import {
  DEMO_SEED,
  DEMO_SESSION_ID,
  SCHEMA,
  SETTLED_MS,
  loadSession,
  writeDoc,
} from '../core/persist'
import { beginWorkingFrom } from '../core/forgotten'
import { makeNode, useStore } from '../core/store'
import { emptySession } from '../core/session'
import { uid } from '../core/id'
import { createResearchState } from '../core/research'
import { appendKnowledgeRevision, captureKnowledgeRevision } from '../core/knowledgeHistory'
import { pulses } from '../core/pulse'
import { BASE } from '../core/tempo'

interface Spec {
  id: string
  parent: string | null
  label: string
  claim: string
  reasoning: string
  kind: NodeKind
  confidence: number
  novelty: number
  tension: number
  evidence?: string[]
  nextSteps?: string[]
  terminal?: boolean
  terminalReason?: string
  /** Absent = shown at load. Present = revealed when that op runs on the parent. */
  reveal?: RunVerb

  /* ── the two pre-authored records ───────────────────────────────────────
     There is deliberately NO `verification` here, and there never will be. A
     pre-authored citation pointing at a real URL is the exact artefact the source
     check exists to make impossible — so the demo cannot carry one, structurally,
     rather than by convention. The seed's survey of the field is the same
     artefact on the seed, and it is absent for the same reason: `specToNode`
     names no field that could carry one. Both records below wear a disclosure
     line in the inspector saying nobody ran them. */

  /** A panel that came back divided, so the split strand is visible with no key. */
  panel?: {
    lenses: Lens[]
    votes: Array<{ lens: Lens; verdict: PanelVerdict; because: string; confidence: number }>
  }
  /** A result, as if the user had run the experiment and come back. */
  outcome?: {
    observed: string
    bearing: Outcome['bearing']
    /** How long ago it was observed. Stamped at reveal so a stored demo ages. */
    daysAgo: number
    source?: string
    confidenceBefore: number
  }
  /** Minutes before now this node was reasoned. Only set where it matters: a child
   *  that predates the result above it is what makes the detached strand real. */
  agedMin?: number
}

const D: Spec[] = [
  /* ── root ───────────────────────────────────────────────────────────── */
  {
    id: 'r',
    parent: null,
    label: DEMO_SEED,
    claim: DEMO_SEED,
    reasoning: '',
    kind: 'seed',
    confidence: 0.5,
    novelty: 0.5,
    tension: 0,
  },

  /* ── branch 1: the word is doing too much work ──────────────────────── */
  {
    id: 'def',
    parent: 'r',
    label: 'Three goals wearing one name',
    claim:
      '"Antigravity" conflates three separate engineering targets — shielding from gravity, propulsion without reaction mass, and reducing effective weight — and they have completely different feasibility.',
    reasoning:
      'Shielding means interposing something that attenuates the field; propulsion means changing momentum without expelling mass; weight reduction means opposing gravity with another force. The first is forbidden by the equivalence principle, the second by momentum conservation unless something is emitted, and the third is routine engineering. Almost every argument about antigravity is really two people using the same word for different targets.',
    kind: 'assumption',
    confidence: 0.92,
    novelty: 0.55,
    tension: 0.2,
    nextSteps: ['Fix which of the three the project actually needs before costing anything'],
  },
  {
    id: 'def-shield',
    parent: 'def',
    label: 'Shielding is forbidden outright',
    claim:
      'No material can attenuate a gravitational field, because the equivalence principle makes free fall locally indistinguishable from inertial motion — there is no field to block.',
    reasoning:
      'Electromagnetic shielding works because charge comes in both signs and a conductor can rearrange it to cancel an external field. Mass-energy has one sign, so there is no analogous rearrangement. More fundamentally, gravity is not a force propagating through a medium but the curvature of the frame the shield itself sits in.',
    kind: 'contradiction',
    confidence: 0.94,
    novelty: 0.15,
    tension: 0.85,
    terminal: true,
    terminalReason: 'Ruled out by the structure of general relativity, not by engineering limits.',
    evidence: ['Equivalence principle, tested to ~1 part in 10^15 by MICROSCOPE (2022)'],
  },
  {
    id: 'def-target',
    parent: 'def',
    label: 'Weight reduction is the live target',
    claim:
      'The only member of the trio that is both permitted and unsolved at useful scale is reducing effective weight cheaply — which reframes the problem from physics to energy economics.',
    reasoning:
      'Levitation against gravity is already routine: buoyancy, aerodynamic lift, magnetic and acoustic levitation all do it. None is forbidden. What none of them does is hold a tonne aloft at a power cost that competes with a rocket. That makes the binding constraint joules per kilogram-second, not any law of physics.',
    kind: 'insight',
    confidence: 0.78,
    novelty: 0.68,
    tension: 0.15,
    nextSteps: [
      'Compute J·kg⁻¹·s⁻¹ for every known levitation mechanism and plot against payload mass',
    ],
  },
  {
    id: 'def-econ',
    parent: 'def-target',
    label: 'Reframe as delta-v economics',
    claim:
      'If the objective is cost per kilogram to orbit rather than "defeating gravity", the antigravity framing is actively harmful — it hides the fact that most of the cost is delta-v, not weight support.',
    reasoning:
      'Reaching orbit is about acquiring ~9.4 km/s of delta-v, not about being lifted. A device that cancelled weight entirely but supplied no horizontal velocity would leave you hovering, still needing nearly the whole energy budget. The romance of the word points effort at the smaller half of the problem.',
    kind: 'implication',
    confidence: 0.85,
    novelty: 0.72,
    tension: 0.3,
    reveal: 'diverge',
  },
  {
    id: 'def-hover',
    parent: 'def-target',
    label: 'Hovering has a floor price',
    claim:
      'Any device holding mass against gravity without touching the ground must impart downward momentum to something, so its minimum power scales with how little mass it can push and how fast.',
    reasoning:
      'For a rotor, P ≈ (mg)^{3/2}/√(2ρA): power falls as disc area grows. That is why helicopters are large and drones are loud. The same trade governs every reactionless-looking scheme — if nothing goes down, nothing stays up.',
    kind: 'mechanism',
    confidence: 0.88,
    novelty: 0.42,
    tension: 0.2,
    reveal: 'diverge',
  },
  {
    id: 'def-static',
    parent: 'def-target',
    label: 'Unless the ground pushes back',
    claim:
      'Static field levitation escapes the momentum floor entirely by transferring load to the Earth through a field — which is why maglev is efficient and hovering is not.',
    reasoning:
      'A maglev train does not accelerate air downward; it pushes against a track. The energy cost is resistive loss, not momentum transfer, and can approach zero with superconductors. The catch is range: field strength falls steeply with distance, so this works at millimetres and not at kilometres.',
    kind: 'insight',
    confidence: 0.82,
    novelty: 0.66,
    tension: 0.25,
    reveal: 'diverge',
  },

  /* ── branch 2: curve spacetime on purpose ───────────────────────────── */
  {
    id: 'metric',
    parent: 'r',
    label: 'Engineer the metric directly',
    claim:
      'General relativity permits locally curved spacetime that would reduce or reverse effective gravitational acceleration in a region; the question is entirely about what source you can put on the right-hand side.',
    reasoning:
      'The field equations are a two-way relation: any metric you can write down corresponds to some stress-energy distribution. So "is this geometry allowed?" is trivially yes, and the whole difficulty collapses into whether the required stress-energy is something matter can actually do.',
    kind: 'mechanism',
    confidence: 0.86,
    novelty: 0.48,
    tension: 0.1,
  },
  {
    id: 'metric-dial',
    parent: 'metric',
    label: 'Stress-energy is the only dial',
    claim:
      'G_μν = (8πG/c⁴) T_μν has exactly one input, so every metric-engineering proposal reduces to producing a specific T_μν — there is no second knob.',
    reasoning:
      'This is why "we just need a new field" proposals rarely survive: any new field still enters through its stress-energy, and stress-energy of ordinary matter is bounded below. The 8πG/c⁴ prefactor is ~2×10⁻⁴³ in SI, which is the real problem — it takes planetary mass-energy to produce laboratory-scale curvature.',
    kind: 'mechanism',
    confidence: 0.95,
    novelty: 0.35,
    tension: 0.15,
    evidence: ['The coupling constant 8πG/c⁴ ≈ 2.08×10⁻⁴³ s²·m⁻¹·kg⁻¹'],
  },
  {
    id: 'metric-coupling',
    parent: 'metric-dial',
    label: 'The prefactor is the whole story',
    claim:
      'Producing 1 m/s² of engineered acceleration over a 10 m region requires stress-energy comparable to a small moon; no material property changes this, because it is set by a universal constant.',
    reasoning:
      'Curvature scales as (8πG/c⁴)·T. Inverting for a laboratory-scale effect gives energy densities around 10^40 J/m³ — some twenty orders of magnitude past a neutron star core. Materials science has no lever here: the constant is not a property of matter.',
    kind: 'contradiction',
    confidence: 0.9,
    novelty: 0.5,
    tension: 0.75,
    terminal: true,
    terminalReason:
      'Any purely positive-energy static source is off by tens of orders of magnitude.',
    reveal: 'deepen',
  },
  {
    id: 'metric-neg',
    parent: 'metric-dial',
    label: 'Negative energy is the escape hatch',
    claim:
      'The only known route to strong curvature at achievable scale is stress-energy that violates the energy conditions — which is why every serious proposal ends up requesting negative energy density.',
    reasoning:
      'Positive-energy sources curve spacetime one way, and the coupling makes that way far too weak. Reversing the sign of the effect requires reversing the sign of the source. Quantum field theory does permit locally negative ⟨T_00⟩, which is the entire reason this line stays open rather than being dismissed.',
    kind: 'implication',
    confidence: 0.82,
    novelty: 0.55,
    tension: 0.35,
    reveal: 'deepen',
  },
  {
    id: 'metric-gm',
    parent: 'metric-dial',
    label: 'Gravitomagnetism is real but tiny',
    claim:
      'Moving mass produces a gravitomagnetic field analogous to magnetism, confirmed by Gravity Probe B — but the lab-scale magnitude is roughly 10⁻²⁰ of the electromagnetic analogue.',
    reasoning:
      'The frame-dragging term is genuine and measured: ~37 milliarcseconds/year from Earth. Scaling it down to a spinning laboratory mass leaves an effect far below any existing measurement floor. It is a real channel with no accessible amplitude.',
    kind: 'gap',
    confidence: 0.87,
    novelty: 0.6,
    tension: 0.3,
    evidence: ['Gravity Probe B frame-dragging result, ~37 mas/yr, published 2011'],
    nextSteps: ['Establish the measurement floor a superconducting gyroscope could reach'],
    reveal: 'deepen',
  },
  {
    id: 'alc',
    parent: 'metric',
    label: 'Alcubierre needs negative energy',
    claim:
      'The 1994 warp metric works as geometry but requires a shell of negative energy density, violating the weak and null energy conditions everywhere in the bubble wall.',
    reasoning:
      'Alcubierre wrote down a metric that contracts space ahead and expands it behind, then solved backwards for the stress-energy it implies. The answer came out negative. This is the standard failure mode of metric engineering: you can always find the geometry, and the source is always the problem.',
    kind: 'implication',
    confidence: 0.93,
    novelty: 0.3,
    tension: 0.45,
    evidence: ['Alcubierre, Class. Quantum Grav. 11 (1994) L73'],
  },
  {
    id: 'alc-ford',
    parent: 'alc',
    label: 'Quantum inequalities bite',
    claim:
      'Ford–Roman inequalities bound how much negative energy can exist and for how long, and applying them to a warp bubble forces the wall thickness to a few hundred Planck lengths.',
    reasoning:
      'Quantum field theory allows negative energy but not freely: the more negative the density, the shorter it can persist. Pfenning and Ford applied this to Alcubierre geometry and found the wall must be absurdly thin, which in turn drives the total energy requirement upward rather than down. The constraint tightens exactly where you need relief.',
    kind: 'contradiction',
    confidence: 0.88,
    novelty: 0.45,
    tension: 0.82,
    terminal: true,
    terminalReason:
      'The bound worsens the energy budget rather than easing it — closing this specific route.',
    evidence: ['Pfenning & Ford, Class. Quantum Grav. 14 (1997) 1743'],
  },
  {
    id: 'alc-budget',
    parent: 'alc',
    label: 'The energy budget collapsed twice',
    claim:
      'Original estimates ran to ~10⁶⁴ kg of negative mass-energy; Van Den Broeck reduced it to a few solar masses by changing the bubble topology, and later claims of a further reduction to sub-tonne scale remain contested.',
    reasoning:
      'The trajectory matters more than any single number. Two independent reformulations each cut the requirement by dozens of orders of magnitude without new physics, purely by changing geometry. That is weak evidence that the number is an artefact of the ansatz rather than a floor — but the remaining requirement is still negative energy, which no reformulation removes.',
    kind: 'evidence',
    confidence: 0.7,
    novelty: 0.62,
    tension: 0.4,
    evidence: [
      'Van Den Broeck, Class. Quantum Grav. 16 (1999) 3973 — topology trick, ~3 solar masses',
    ],
    nextSteps: [
      'Check whether the reduction sequence has a demonstrated lower bound or just stops',
    ],
  },
  {
    id: 'alc-broeck',
    parent: 'alc-budget',
    label: 'Van Den Broeck buys volume, not energy',
    claim:
      'The topology trick works by making the bubble interior large while its outer surface stays microscopic — it reduces the required energy by shrinking what must be wrapped, not by needing less exotic matter per unit area.',
    reasoning:
      'The construction inserts a "pocket" whose internal volume vastly exceeds what its exterior suggests. Energy scales with the surface being manipulated, so a small neck is cheap. The exotic-matter requirement per unit area is untouched, which means the qualitative barrier survives the quantitative collapse.',
    kind: 'mechanism',
    confidence: 0.78,
    novelty: 0.7,
    tension: 0.3,
    reveal: 'deepen',
  },
  {
    id: 'alc-contested',
    parent: 'alc-budget',
    label: 'The sub-tonne claim is contested',
    claim:
      'The widely-repeated reduction to roughly spacecraft mass comes from an oscillating-toroidal reformulation that has not been independently reproduced and is disputed in the literature.',
    reasoning:
      'It circulated far outside the peer-reviewed record and is frequently cited without its caveats. Treating it as established is the single most common error in popular discussion of this topic. Downgrade any argument that leans on it.',
    kind: 'contradiction',
    confidence: 0.72,
    novelty: 0.5,
    tension: 0.7,
    reveal: 'deepen',
  },
  {
    id: 'soliton',
    parent: 'metric',
    label: 'Positive-energy solitons exist',
    claim:
      'A 2024 result constructed a constant-velocity subluminal warp soliton satisfying all energy conditions — the first warp-like geometry requiring no exotic matter at all.',
    reasoning:
      'The construction combines a stable shell of ordinary matter with a shift vector, showing the exotic-matter requirement was an artefact of demanding superluminal travel, not intrinsic to warp geometry. This is the single most important recent development on this branch: it moves the question from "is it forbidden?" to "is it affordable?", which is a different kind of problem entirely.',
    kind: 'evidence',
    confidence: 0.74,
    novelty: 0.92,
    tension: 0.2,
    evidence: [
      'Fuchs et al., "Constant velocity physical warp drive solution", Class. Quantum Grav. (2024)',
    ],
    nextSteps: ['Read the energy-condition proof directly rather than the press coverage'],
  },
  {
    id: 'soliton-sub',
    parent: 'soliton',
    label: 'Subluminal — and that is fine',
    claim:
      'The positive-energy solutions are strictly subluminal, which removes the causality objections that made superluminal warp metrics unphysical in the first place.',
    reasoning:
      'Most of the theoretical trouble with warp drives — closed timelike curves, causality violation, semiclassical instability at the horizon — enters with superluminal motion. A subluminal soliton inherits none of it. What remains is an engineering problem about mass and stability rather than a foundational one.',
    kind: 'implication',
    confidence: 0.8,
    novelty: 0.65,
    tension: 0.1,
  },
  {
    id: 'soliton-accel',
    parent: 'soliton',
    label: 'Nobody can accelerate it',
    claim:
      'The solution is constructed at constant velocity; no known method takes such a soliton from rest to speed, and the transition may reintroduce the energy-condition violations that were avoided.',
    reasoning:
      'A constant-velocity solution is a snapshot of a steady state, not a trajectory. Acceleration means a time-dependent metric, and time dependence is exactly where negative energy tends to reappear in these constructions. Until someone writes down an accelerating solution, this branch is a proof of possibility rather than a route.',
    kind: 'gap',
    confidence: 0.83,
    novelty: 0.78,
    tension: 0.55,
    nextSteps: [
      'Look for any published accelerating solution that preserves the energy conditions',
      'Estimate whether the transition energy scales with total mass or with acceleration',
    ],
  },
  {
    id: 'soliton-mass',
    parent: 'soliton',
    label: 'Still asks for planetary mass',
    claim:
      'Satisfying the energy conditions does not make the requirement small — the shell mass in these solutions remains astronomical, trading impossible matter for unavailable quantities of ordinary matter.',
    reasoning:
      'Removing the exotic-matter requirement changes the character of the problem without changing its scale, because the coupling constant is unchanged. This is progress in kind, not degree: "needs something that cannot exist" becomes "needs more mass than we can assemble", and only the first of those is permanently closed.',
    kind: 'contradiction',
    confidence: 0.81,
    novelty: 0.6,
    tension: 0.72,
    reveal: 'challenge',
  },
  {
    id: 'soliton-stab',
    parent: 'soliton',
    label: 'Stability is unproven',
    claim:
      'The solutions are shown to exist, not shown to be stable — a shell configuration that satisfies the energy conditions instantaneously may still be dynamically unstable on short timescales.',
    reasoning:
      'Existence proofs in GR routinely produce configurations that perturbation analysis then destroys. Until someone runs the linear stability analysis, the honest status is "a solution exists" rather than "a structure could persist". This is the cheapest possible next step and it has not been published.',
    kind: 'gap',
    confidence: 0.79,
    novelty: 0.7,
    tension: 0.45,
    nextSteps: ['Linear perturbation analysis of the published shell configuration'],
    reveal: 'challenge',
  },
  {
    id: 'soliton-materials',
    parent: 'soliton',
    label: 'Metric engineering as materials science',
    claim:
      'Once exotic matter is out of the picture, the entire problem becomes finding matter configurations with extreme, sustained, controllable stress anisotropy — which is a materials question with a real research community.',
    reasoning:
      'The positive-energy solutions need shells under enormous directed stress rather than substances that cannot exist. That relocates the field from theoretical GR into condensed matter and high-pressure physics, where progress is incremental but continuous. Reframing a dead-looking problem into a live discipline is usually where the leverage is.',
    kind: 'insight',
    confidence: 0.66,
    novelty: 0.88,
    tension: 0.25,
    reveal: 'deepen',
  },

  /* ── branch 3: quantum vacuum ───────────────────────────────────────── */
  {
    id: 'vac',
    parent: 'r',
    label: 'Mine the quantum vacuum',
    claim:
      'The vacuum has measurable structure and can be locally driven to negative energy density, so it is the one physically real source of the exotic stress-energy metric engineering asks for.',
    reasoning:
      'This is not speculation: Casimir showed that boundary conditions shift vacuum energy, and the effect has been measured to percent-level accuracy. The question is whether the magnitude and geometry are ever useful, not whether the phenomenon is real.',
    kind: 'mechanism',
    confidence: 0.72,
    novelty: 0.6,
    tension: 0.2,
  },
  {
    id: 'vac-casimir',
    parent: 'vac',
    label: 'Casimir energy really is negative',
    claim:
      'Between parallel plates the vacuum energy density is negative, u = −π²ħc/720d⁴, and the resulting force has been measured directly.',
    reasoning:
      'The negative sign is not a bookkeeping convention — it produces an attractive force that has been measured with torsion pendulums and AFM cantilevers since Lamoreaux in 1997. Whatever else is contested here, the existence of locally negative energy density is not.',
    kind: 'evidence',
    confidence: 0.93,
    novelty: 0.3,
    tension: 0.1,
    evidence: ['Lamoreaux, Phys. Rev. Lett. 78 (1997) 5 — force measured to ~5%'],
  },
  {
    id: 'vac-scale',
    parent: 'vac-casimir',
    label: 'The d⁻⁴ scaling kills it',
    claim:
      'At 10 nm separation the Casimir energy density is around −10⁵ J/m³ — some forty-five orders of magnitude short of what metric engineering requires, and it falls as the fourth power of spacing.',
    reasoning:
      'Every order of magnitude of useful volume costs four orders of magnitude of energy density. The scaling runs in exactly the wrong direction: the regime where the effect is strong is the regime where there is no room to put anything. No fabrication advance changes an exponent.',
    kind: 'contradiction',
    confidence: 0.91,
    novelty: 0.55,
    tension: 0.85,
    terminal: true,
    terminalReason: 'The scaling law forbids the useful regime independently of manufacturing.',
    // A panel that came back 2–1. Deliberately on a node that is also a terminal
    // contradiction, because that is the case worth being able to see: the strand
    // parts, the disc keeps its cross, and the reading that the path is exhausted
    // sits next to three lenses that did not agree it is.
    panel: {
      lenses: ['first-principles', 'empirical', 'systems'],
      votes: [
        {
          lens: 'first-principles',
          verdict: 'holds',
          because:
            'The exponent is structural. u ∝ d⁻⁴ comes out of the mode sum, not out of a material property, so no fabrication advance touches it. Forty-five orders is not a gap you close.',
          confidence: 0.9,
        },
        {
          lens: 'empirical',
          verdict: 'holds-weaker',
          because:
            'It holds for the parallel-plate geometry the number was computed in, which the claim does not say. Measured Casimir energies in non-trivial geometries depart from the plate result by factors of a few, and nobody has measured the regimes this argument extrapolates through.',
          confidence: 0.64,
        },
        {
          lens: 'systems',
          verdict: 'holds',
          because:
            'Worse than stated, in fact: the useful-volume requirement and the spacing requirement pull in opposite directions, so the two constraints multiply rather than trade off.',
          confidence: 0.83,
        },
      ],
    },
  },
  {
    id: 'vac-ledger',
    parent: 'vac-casimir',
    label: 'The total ledger stays positive',
    claim:
      'Assembling the plates costs more positive energy than the negative region contains, so the vacuum is not a source of net negative energy — only of local rearrangement.',
    reasoning:
      'This is the objection that survives every proposed improvement. The negative density exists relative to the unbounded vacuum, but the boundary conditions creating it are made of ordinary matter with ordinary positive mass-energy. Integrated over the whole apparatus, the sign flips back.',
    kind: 'contradiction',
    confidence: 0.87,
    novelty: 0.5,
    tension: 0.8,
  },
  {
    id: 'vac-dyn',
    parent: 'vac',
    label: 'Dynamical Casimir radiates',
    claim:
      'Moving a boundary at relativistic speed converts virtual photons to real ones — observed in 2011 in a superconducting circuit — proving the vacuum can be driven, not merely shaped.',
    reasoning:
      'A SQUID acting as a mirror moving at a few percent of light speed produced measurable microwave photons out of vacuum. This is a genuinely different capability from the static Casimir effect: it shows energy can be extracted from a driven boundary. Whether that pathway scales to anything gravitational is entirely unestablished.',
    kind: 'evidence',
    confidence: 0.85,
    novelty: 0.82,
    tension: 0.2,
    evidence: ['Wilson et al., Nature 479 (2011) 376'],
    nextSteps: [
      'Establish the energy conversion efficiency and how it scales with drive frequency',
    ],
  },
  {
    id: 'vac-exp',
    parent: 'vac-casimir',
    label: 'Weigh a Casimir cavity',
    claim:
      'A direct test: measure whether a rigid Casimir cavity weighs less than the sum of its parts by the mass-equivalent of its negative energy content.',
    reasoning:
      'The prediction is unambiguous — negative energy should gravitate negatively, so a cavity should be measurably lighter than its disassembled components. The effect is around 10⁻²⁶ of the apparatus mass, which is below current torsion-balance sensitivity but not by an inconceivable margin. A null result at improved sensitivity would be genuinely informative.',
    kind: 'experiment',
    confidence: 0.68,
    novelty: 0.8,
    tension: 0.3,
    nextSteps: [
      'Compute the required balance sensitivity for a stacked-cavity array',
      'Check whether anyone has attempted this since the 2000s proposals',
    ],
    // Deliberately NOT a reserve. This is the one node in the demo that has touched
    // reality, and the two marks it carries — the result bar and the magenta strike
    // — are the grammar a reader most needs to learn. They have to be on screen
    // before anyone runs anything. `vac-exp2` still covers the Test affordance.
    outcome: {
      observed:
        'Built the 24-plate stacked array to buy sensitivity and ran it 60 hours at 4 K. No mass deficit, but that is not the finding: the stack introduced a thermal-gradient systematic that scales with plate count, so every plate added costs more noise than it buys signal. The route to sensitivity runs backwards. Single-cavity runs are the only ones worth doing, and those are 10⁻²⁶ away, not "not inconceivable".',
      bearing: 'refuted',
      daysAgo: 9,
      source: 'balance log 2026-07, runs 41–48',
      confidenceBefore: 0.68,
    },
  },
  {
    id: 'vac-exp-bound',
    parent: 'vac-exp',
    label: 'A null result bounds the coupling',
    claim:
      'Whatever sensitivity the balance reaches, a null result converts directly into an upper bound on how strongly negative energy can gravitate — which is a publishable number even though it is not the effect.',
    reasoning:
      'This is the argument that makes the experiment worth running regardless of outcome: the measurement is a bound either way, and bounds are what actually constrain the metric-engineering branch above. Reasoned before the run came back, which is why it does not yet mention the noise floor that run actually reached.',
    kind: 'implication',
    confidence: 0.74,
    novelty: 0.45,
    tension: 0.15,
    // Reasoned before the result came in. This is what the detached strand means,
    // and it is why the demo can show it without anyone running anything.
    agedMin: 180,
  },
  {
    id: 'vac-exp2',
    parent: 'vac-casimir',
    label: 'Look for anomalous inertia',
    claim:
      'A cheaper falsification: drive a Casimir stack on a torsion pendulum and look for a discrepancy between its inertial and gravitational mass.',
    reasoning:
      'Inertial-mass measurement sidesteps the need for absolute weight precision by turning the question into a differential one, which is where torsion balances excel. Any discrepancy would be enormous news; a null result at part-in-10¹² would tighten a bound nobody has tightened. The apparatus is undergraduate-scale.',
    kind: 'experiment',
    confidence: 0.62,
    novelty: 0.75,
    tension: 0.3,
    reveal: 'experiment',
  },

  /* ── branch 4: the electromagnetic shortcut ─────────────────────────── */
  {
    id: 'em',
    parent: 'r',
    label: 'Electromagnetic shortcuts',
    claim:
      'A long line of proposals claims coupling between electromagnetic fields and gravity strong enough to produce thrust or weight change in a laboratory.',
    reasoning:
      'This branch is worth mapping precisely because it is where most public attention goes and where essentially all of it has failed. Understanding *how* it failed is more useful than the individual claims.',
    kind: 'mechanism',
    confidence: 0.35,
    novelty: 0.25,
    tension: 0.5,
  },
  {
    id: 'em-momentum',
    parent: 'em',
    label: 'Momentum conservation is the wall',
    claim:
      'Any device producing sustained thrust without emitting something is inconsistent with momentum conservation, which follows from spatial translation symmetry rather than from any particular theory.',
    reasoning:
      "Noether's theorem ties conservation of momentum to the homogeneity of space. Overturning it requires space itself to be inhomogeneous at laboratory scale, which is a far larger claim than any thruster paper makes. This is why reactionless-drive results are treated as measurement errors until proven otherwise — the prior is set by a symmetry, not by scepticism.",
    kind: 'contradiction',
    confidence: 0.95,
    novelty: 0.2,
    tension: 0.9,
  },
  {
    id: 'em-noether',
    parent: 'em-momentum',
    label: 'Invert it: what if space were inhomogeneous?',
    claim:
      'Taking the objection seriously in reverse: momentum non-conservation would require a preferred spatial structure — and cosmology already supplies one at large scale.',
    reasoning:
      'Momentum is not globally conserved in general relativity precisely because expanding spacetime lacks time-translation symmetry; this is why photons redshift. The effect is real but scales with the Hubble parameter, giving ~10⁻¹⁸ s⁻¹ — utterly negligible in a laboratory. The escape hatch exists and is far too small to use.',
    kind: 'insight',
    confidence: 0.76,
    novelty: 0.86,
    tension: 0.4,
    reveal: 'invert',
  },
  {
    id: 'em-emdrive',
    parent: 'em',
    label: 'EmDrive resolved as thermal',
    claim:
      'The most careful replication, at Dresden in 2021, found the apparent thrust scaled with heating of the mount and vanished when thermal effects were controlled.',
    reasoning:
      'This is the cleanest possible outcome for a contested result: not a failure to replicate, but a positive identification of the artefact. The signal was real, the interpretation was not. It is also the standard failure mode for this entire class of experiment — micronewton forces and thermal expansion are hard to separate.',
    kind: 'contradiction',
    confidence: 0.89,
    novelty: 0.4,
    tension: 0.85,
    terminal: true,
    terminalReason: 'Mechanism identified and accounted for; nothing left to explain.',
    evidence: ['Tajmar et al., Dresden replication, 2021'],
  },
  {
    id: 'em-mach',
    parent: 'em',
    label: 'Mach-effect thrusters unresolved',
    claim:
      "Woodward's Mach-effect devices claim micronewton thrust from transient mass fluctuations; independent testing found signals consistent with thermal and vibrational coupling but has not closed the question.",
    reasoning:
      'Unlike the EmDrive this has a stated theoretical mechanism — inertia as a gravitational interaction with distant matter — which makes it falsifiable in a way most claims are not. The experimental status is genuinely ambiguous rather than settled, which is rare enough here to be worth noting.',
    kind: 'gap',
    confidence: 0.45,
    novelty: 0.55,
    tension: 0.6,
    nextSteps: ['Find the most recent independent replication and its stated systematic floor'],
  },
  {
    id: 'em-podkletnov',
    parent: 'em',
    label: 'Superconductor shielding failed',
    claim:
      'The 1992 claim of ~0.05–2% weight reduction above a rotating superconducting disc did not survive replication attempts, including a dedicated NASA-funded effort.',
    reasoning:
      'Multiple groups built the apparatus and found no effect at sensitivities well below the claimed magnitude. The claim persists in popular accounts almost entirely on the strength of the original report. Treat it as closed.',
    kind: 'contradiction',
    confidence: 0.86,
    novelty: 0.25,
    tension: 0.8,
    terminal: true,
    terminalReason: 'Replication attempts null at sensitivities far below the claimed effect.',
    evidence: ['Li et al. (NASA Marshall) null result, 1997; Hathaway et al., Physica C, 2003'],
  },

  /* ── branch 5: inertia rather than gravity ──────────────────────────── */
  {
    id: 'inertia',
    parent: 'r',
    label: 'Attack inertia instead',
    claim:
      'If effective inertial mass could be reduced, the same thrust would produce far more acceleration — capturing most of the practical benefit of antigravity without touching gravity at all.',
    reasoning:
      "This sidesteps the equivalence-principle objection by targeting the other side of F = ma. It is a genuinely different attack surface, and it is the reframing most likely to be productive, because inertia's origin is less settled than gravity's.",
    kind: 'analogy',
    confidence: 0.42,
    novelty: 0.85,
    tension: 0.4,
  },
  {
    id: 'inertia-equiv',
    parent: 'inertia',
    label: 'But the two are locked together',
    claim:
      'Inertial and gravitational mass are experimentally identical to one part in 10¹⁵, so any mechanism reducing one should reduce the other — collapsing the reframe.',
    reasoning:
      'MICROSCOPE\'s 2022 result is the tightest test to date and found no violation. Unless the equivalence is broken, "reduce inertia" and "reduce weight" are the same request wearing different clothes, and the reframe buys nothing.',
    kind: 'contradiction',
    confidence: 0.9,
    novelty: 0.35,
    tension: 0.85,
    evidence: ['MICROSCOPE final result, Phys. Rev. Lett. 129 (2022) 121102'],
  },
  {
    id: 'inertia-diamag',
    parent: 'inertia',
    label: 'Diamagnetic levitation already works',
    claim:
      'Any material with water content levitates in a strong enough gradient — famously a live frog at about 16 T — demonstrating that opposing gravity is engineering, not physics.',
    reasoning:
      'Worth holding onto as a calibration point: the thing people imagine antigravity looking like has been done, in a lab, since 1997. What makes it unusable is that field energy scales as B² and the required volume scales with the object, so a person-sized version is a power-plant-sized magnet.',
    kind: 'evidence',
    confidence: 0.92,
    novelty: 0.45,
    tension: 0.15,
    evidence: ['Berry & Geim, Eur. J. Phys. 18 (1997) 307'],
  },
  {
    id: 'inertia-unruh',
    parent: 'inertia',
    label: 'Inertia from the vacuum?',
    claim:
      'Several programmes derive inertia as a reaction to vacuum or horizon effects, which would in principle make it an engineerable quantity — but none has produced a testable laboratory prediction.',
    reasoning:
      'If inertia is emergent from an interaction with the vacuum rather than intrinsic, modifying the local vacuum would modify it. That is a genuinely interesting conditional. The problem is that these programmes have consistently failed to yield a prediction that distinguishes them from standard physics at accessible scales, which leaves the branch alive but inert.',
    kind: 'gap',
    confidence: 0.3,
    novelty: 0.9,
    tension: 0.5,
    nextSteps: ['Find any published prediction of these programmes that a lab could falsify'],
  },
]

const byId = new Map(D.map((s) => [s.id, s]))

const MIN = 60_000
const DAY = 24 * 60 * MIN

function specToNode(s: Spec) {
  const now = Date.now()
  // Stamped at reveal rather than written as literals, so a stored demo keeps
  // reading correctly however long it sits on disk — and so the child that
  // predates the result really does predate it, by wall clock, on every load.
  const createdAt = s.agedMin ? now - s.agedMin * MIN : now
  return makeNode({
    id: s.id,
    parentId: s.parent,
    label: s.label,
    claim: s.claim,
    reasoning: s.reasoning,
    kind: s.kind,
    confidence: s.confidence,
    novelty: s.novelty,
    tension: s.tension,
    evidence: s.evidence ?? [],
    nextSteps: s.nextSteps ?? [],
    terminal: s.terminal ?? false,
    terminalReason: s.terminalReason,
    origin: s.reveal ?? 'seed',
    createdAt,
    ...(s.panel
      ? {
          panel: {
            lenses: s.panel.lenses,
            votes: s.panel.votes,
            // Held fixed across the seats, and recorded because holding it fixed is
            // what makes a disagreement attributable to the lens.
            temper: 'balanced' as const,
            checkedAt: now,
          },
        }
      : {}),
    ...(s.outcome
      ? {
          outcome: {
            observed: s.outcome.observed,
            bearing: s.outcome.bearing,
            at: now - s.outcome.daysAgo * DAY,
            // Later than the aged child above, earlier than now.
            recordedAt: now - 30 * MIN,
            confidenceBefore: s.outcome.confidenceBefore,
            ...(s.outcome.source ? { source: s.outcome.source } : {}),
          },
        }
      : {}),
  })
}

/** Hoisted so the live ticker and the retained trace can never drift apart. */
const DEMO_SEED_TRACE =
  'Taking the premise apart. The word "antigravity" is carrying three different engineering targets — separating them first, because they have very different feasibility.'

/**
 * The unfurl in flight: the opening's, or a reserve's. One at a time, because
 * both hold `busy`, and each is handed to the store as its op's `interrupt`.
 */
interface Unfurl {
  opId: string
  /** What has actually landed, so a stop records what the graph holds. */
  produced: string[]
  thinking?: string
  /** Cut the beat in progress short. */
  wake: (() => void) | null
}
let unfurling: Unfurl | null = null

/** One beat of an unfurl, which a stop ends early. */
function nap(u: Unfurl, ms: number): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      u.wake = null
      resolve()
    }, ms)
    u.wake = () => {
      clearTimeout(timer)
      u.wake = null
      resolve()
    }
  })
}

/** End an unfurl's op with what it actually landed. */
function settle(u: Unfurl): void {
  const st = useStore.getState()
  // Only on the document it began on. `restoreSession` putting another
  // exploration on screen takes the lock down with the old graph, and an op
  // ending written there would add a revision about the demo to its history.
  if (!st.session.ops.some((o) => o.id === u.opId)) return
  st.endOp(u.opId, u.produced.slice(), u.thinking ? { thinking: u.thinking } : undefined)
}

/**
 * Stop an in-progress demo unfurl: the Stop control, and every door that
 * yields to the unfurl rather than refusing (`yieldUnpaid`, which reaches this
 * as the op's `interrupt`).
 *
 * SETTLED BEFORE IT RETURNS, which is what lets a door go through on its next
 * line. The op is ended here, on the demo's own document, naming the nodes that
 * actually landed, and the lock goes with it. The loop parked in its beat is
 * woken, finds it is no longer the unfurl in flight, and returns without
 * touching the store again — so nothing it owned acts after the door has moved
 * on, and a checkpoint of the demo taken on the next line holds a finished op.
 */
export function cancelDemo(): void {
  const u = unfurling
  if (!u) return
  unfurling = null
  // A replay pressed straight after a stop starts a fresh load rather than
  // handing back the one just stopped. A load in flight is always the unfurl
  // being stopped here: `loadDemo` stops a reserve's before it begins its own.
  demoLoading = null
  u.wake?.()
  settle(u)
}

/** Whether every node of the opening is on the graph: what the authored line
 *  spoken under it describes (`narrator.ts`), and what a stop cuts short. */
export function demoOpeningLanded(s: Pick<Session, 'nodes'>): boolean {
  return D.every((d) => d.reveal !== undefined || s.nodes[d.id] !== undefined)
}

/**
 * Whether a result on the worked example is the one written into it — the node,
 * the words, the bearing and the source this file authored — rather than one a
 * reader recorded there.
 *
 * Recording is free, so it never remints the demo: a reader's result lands
 * under the same id as the authored one, and a note saying "nobody ran this
 * experiment" over it would be the one untrue sentence on the card. Read off the
 * words themselves, so an Edit that changes them makes the result the reader's.
 */
export function isAuthoredDemoOutcome(
  nodeId: string,
  o: Pick<Outcome, 'observed' | 'bearing' | 'source'> | undefined,
): boolean {
  const authored = byId.get(nodeId)?.outcome
  return (
    !!authored &&
    !!o &&
    o.observed === authored.observed &&
    o.bearing === authored.bearing &&
    (o.source ?? '') === (authored.source ?? '')
  )
}

/** True if this parent has an unrevealed branch for this operation. */
export function hasDemoReserve(parentId: string, op: RunVerb): boolean {
  const s = useStore.getState().session
  if (!byId.has(parentId)) return false
  return D.some((d) => d.parent === parentId && d.reveal === op && !s.nodes[d.id])
}

/** Any unrevealed branch at all, regardless of operation. */
export function demoOpsFor(parentId: string): RunVerb[] {
  const s = useStore.getState().session
  const ops = new Set<RunVerb>()
  for (const d of D) {
    if (d.parent === parentId && d.reveal && !s.nodes[d.id]) ops.add(d.reveal)
  }
  return [...ops]
}

export async function revealDemo(parentId: string, op: RunVerb): Promise<string[]> {
  const beat = BASE.beat / useStore.getState().tempo
  const store = useStore.getState()
  const pending = D.filter(
    (d) => d.parent === parentId && d.reveal === op && !store.session.nodes[d.id],
  )
  if (!pending.length) return []

  // A step of history, so undo works in demo mode too — which is where most
  // people will first reach for it.
  store.mark(OP_LABEL[op])

  const opId = store.beginOp(op, [parentId], { spends: false, interrupt: cancelDemo })
  const u: Unfurl = { opId, produced: [], wake: null }
  unfurling = u

  // Mirror the live path exactly, including the thinking ticker — the demo
  // should teach the real rhythm of the tool, not a simplified one.
  const tick = 300 / useStore.getState().tempo
  const ticker = [
    'Reading the branch from the root down…',
    'Checking which moves this node has already made…',
    'Looking for the constraint that actually binds…',
    'Discarding restatements of the parent…',
  ]
  for (let i = 0; i < ticker.length; i++) {
    useStore.getState().setThought(ticker.slice(0, i + 1).join(' '))
    await nap(u, tick)
    if (unfurling !== u) return u.produced.slice()
  }

  for (const spec of pending) {
    useStore.getState().addNodes([specToNode(spec)])
    pulses.mark([parentId])
    u.produced.push(spec.id)
    await nap(u, beat)
    if (unfurling !== u) return u.produced.slice()
  }

  unfurling = null
  settle(u)
  return u.produced.slice()
}

/**
 * The worked example's opening graph, built at once — every node the opening
 * unfurl lands, none of the reserves, and no verification, which the demo
 * never carries. `runDemo` below is this same set landed on a beat; this is for
 * the marks harness (`evals/marks`), which needs a real graph to ask about and
 * not the show.
 */
export function demoOpeningGraph(): {
  seed: string
  nodes: Record<string, ReasonNode>
  order: string[]
} {
  const initial = D.filter((d) => !d.reveal)
  return {
    seed: DEMO_SEED,
    nodes: Object.fromEntries(initial.map((d) => [d.id, specToNode(d)])),
    order: initial.map((d) => d.id),
  }
}

let demoLoading: Promise<void> | null = null

/**
 * Load the demo exploration, unfurling it the way a live run would.
 *
 * Single-flight: StrictMode invokes mount effects twice in development, and a
 * user can hit "Replay" twice. Two concurrent loaders would each clear the
 * other's session halfway through and leave the graph permanently mid-build.
 *
 * It replaces whatever is open, so it takes the lock `newExploration` takes:
 * a reserve still unfurling is stopped, and a paid pass or a live run refuses
 * — installing the demo over one would take down the lock it is holding.
 */
export function loadDemo(): Promise<void> {
  if (demoLoading) return demoLoading
  const st = useStore.getState()
  if ((st.run !== null && st.run.phase !== 'ended') || !st.yieldUnpaid()) {
    st.toast('One thought at a time — let the current branch finish.', 'warn')
    return Promise.resolve()
  }
  const loading: Promise<void> = runDemo().finally(() => {
    // Not a later load's: a stop lets a replay start before this one returns.
    if (demoLoading === loading) demoLoading = null
  })
  demoLoading = loading
  return loading
}

/**
 * The worked example as it stands before its opening unfurls: the seed alone,
 * under `s_demo` from the start.
 *
 * The session id is pinned only while the demo is pristine. The first op that
 * runs with a live key re-mints it, so a branched demo becomes a normal
 * exploration with its own stored document — and replaying the demo
 * overwrites only the untouched demo.
 */
function demoSession(): Session {
  const root = D.find((d) => d.parent === null)!
  return {
    ...emptySession(DEMO_SEED),
    id: DEMO_SESSION_ID,
    // The name the owner's design calls the worked example by, in the sidebar's
    // row and the thread's breadcrumb (D362): a label over the seed, never an
    // edit of it (D105), so every pass still reads the seed's own words.
    name: 'Antigravity device',
    nodes: { [root.id]: { ...specToNode(root), createdOrdinal: 0 } },
    order: [root.id],
    nextNodeOrdinal: 1,
  }
}

async function runDemo(): Promise<void> {
  const beat = BASE.beat / useStore.getState().tempo
  const root = D.find((d) => d.parent === null)!
  const initial = D.filter((d) => !d.reveal)

  // A BRAND-NEW document under an id this tab may already hold a stale forget
  // baseline for. `s_demo` is the one id the app reuses, so it is the one where
  // "forgotten in another tab" can otherwise stick for the life of the tab and
  // silently refuse every transcript this replay goes on to produce. The only
  // call site, because it is the only place a new document lands under a name
  // that has been used before. See `core/forgotten.ts`. Taken BEFORE the
  // document lands, so its first save is judged against the new baseline.
  beginWorkingFrom(DEMO_SESSION_ID)

  // Built whole under its own id and installed in ONE write. It used to be
  // seeded under a fresh id by `startSession` and re-id'd a line later, and the
  // autosave checkpoints both sides of every id change — so the throwaway was
  // committed as a 0-node copy of this seed, one more on every replay, in
  // Recent and in the library (`isDemoPhantom` in `core/persist.ts` takes down
  // what that left). One transition, from whatever was open straight to
  // `s_demo`, leaves the checkpoint nothing to keep but the exploration being
  // left. Not a `startSession` either way: that carries a blank canvas's
  // project into the seed, and the worked example is nobody's filing.
  const st = useStore.getState()
  st.restoreSession({
    v: SCHEMA,
    savedAt: Date.now(),
    session: demoSession(),
    ui: { selectedId: root.id, focusId: null, lens: st.lens, temper: st.temper },
  })
  // A new exploration's door, like `startSession`'s: the example unfurls in the
  // thread, and `restoreSession` rightly keeps whatever tab a reopening had.
  useStore.getState().setRightTab('node')

  const opId = useStore
    .getState()
    .beginOp('expand', [root.id], { spends: false, interrupt: cancelDemo })
  const u: Unfurl = { opId, produced: [root.id], thinking: DEMO_SEED_TRACE, wake: null }
  unfurling = u
  useStore.getState().setThought(DEMO_SEED_TRACE)

  for (const spec of initial) {
    if (spec.parent === null) continue
    useStore.getState().addNodes([specToNode(spec)])
    pulses.mark([spec.parent])
    u.produced.push(spec.id)
    await nap(u, beat)
    if (unfurling !== u) return
  }

  unfurling = null
  settle(u)
}

/* ── Repair ─────────────────────────────────────────────────────────────── */

/** The opening's op: the unpaid Branch on the seed that `runDemo` records. */
const openingOf = (s: Session, rootId: string): Op | undefined =>
  s.ops.find((o) => o.kind === 'expand' && o.model === undefined && o.targetIds[0] === rootId)

/**
 * The worked example with the opening's missing nodes put back, or null when
 * nothing is missing or the document is not one this can vouch for.
 *
 * MISSING MEANS NEVER LANDED. A finished opening names what it landed; a node
 * it names that the graph lacks was pruned by hand, and stays pruned. An
 * opening with no end — the tab closed under it — or no op at all — closed
 * before its first save — landed nothing anybody took away. A node whose parent
 * is gone is not put back either: there is nothing to hang it on, and the store
 * never restores a dangling edge. Revealed branches are not the opening's and
 * are left exactly as they are.
 *
 * The opening is then made whole the way an uninterrupted one ends: finished,
 * naming every node it holds, with its trace. The nodes are stamped settled, as
 * the codec stamps any restored node, so they are on screen consolidated rather
 * than growing in; and the board takes one knowledge revision saying why it
 * changed, as `endOp` would have written.
 */
export function withDemoRestored(s: Session, now = Date.now()): Session | null {
  const root = D.find((d) => d.parent === null)!
  if (s.id !== DEMO_SESSION_ID || s.nodes[root.id]?.parentId !== null) return null
  const initial = D.filter((d) => !d.reveal)
  const opening = openingOf(s, root.id)
  const landed = new Set(opening?.finishedAt !== undefined ? opening.produced : [])
  const nodes = { ...s.nodes }
  const order = s.order.slice()
  let ordinal =
    s.nextNodeOrdinal ??
    s.order.reduce((max, id, index) => Math.max(max, s.nodes[id]?.createdOrdinal ?? index), -1) + 1
  const settledAt = now - SETTLED_MS
  for (const spec of initial) {
    if (spec.parent === null || nodes[spec.id] || landed.has(spec.id)) continue
    if (!nodes[spec.parent]) continue
    nodes[spec.id] = {
      ...specToNode(spec),
      createdOrdinal: ordinal++,
      birth: settledAt,
      lastPulse: settledAt,
    }
    order.push(spec.id)
  }
  if (order.length === s.order.length) return null

  const produced = initial.map((d) => d.id).filter((id) => nodes[id] || landed.has(id))
  const finished: Op = opening
    ? {
        ...opening,
        produced,
        finishedAt: opening.finishedAt ?? now,
        thinking: opening.thinking ?? DEMO_SEED_TRACE,
      }
    : {
        id: uid('op'),
        kind: 'expand',
        targetIds: [root.id],
        startedAt: s.createdAt,
        finishedAt: now,
        produced,
        thinking: DEMO_SEED_TRACE,
      }
  const ops = opening ? s.ops.map((o) => (o === opening ? finished : o)) : [finished, ...s.ops]
  const base: Session = {
    ...s,
    nodes,
    order,
    nextNodeOrdinal: ordinal,
    ops,
    research: s.research ?? createResearchState({ id: `research_${s.id}`, now: s.createdAt }),
  }
  const revision = captureKnowledgeRevision(base, {
    id: uid('known'),
    recordedAt: now,
    reason: 'Worked example restored',
    eventId: finished.id,
  })
  return {
    ...base,
    research: {
      ...base.research!,
      knowledgeHistory: appendKnowledgeRevision(base.research!.knowledgeHistory, revision),
    },
  }
}

/**
 * Put back what an interrupted opening never reached. Once, at boot.
 *
 * A tab closed a few seconds into the unfurl left `s_demo` holding its seed and
 * whatever had landed, and the first-run check replays the demo only over an
 * EMPTY canvas — so every later visit opened on the same stub. A door that
 * stopped the unfurl to go somewhere else leaves the same shape behind. Both
 * are the example's own nodes, missing through no act of the visitor's, so they
 * come back from the spec beside any branch the visitor revealed.
 *
 * The open document is replaced in one write and the autosave keeps it like any
 * edit; one that is only on the shelf is written aside, through the forget
 * guard and the version ordering every such write has. Returns whether anything
 * was put back.
 */
export function repairDemo(): boolean {
  const st = useStore.getState()
  if (st.session.id === DEMO_SESSION_ID) {
    // An opening still unfurling finishes itself.
    if (st.busy) return false
    const session = withDemoRestored(st.session)
    if (!session) return false
    st.restoreSession({
      v: SCHEMA,
      savedAt: Date.now(),
      session,
      ui: { selectedId: st.selectedId, focusId: st.focusId, lens: st.lens, temper: st.temper },
    })
    return true
  }
  const doc = loadSession(DEMO_SESSION_ID)
  const session = doc ? withDemoRestored(doc.session) : null
  if (!doc || !session) return false
  // Stamped past the version it read, like every write aside: the stamp is the
  // shelf's version, and one it already holds refuses the write. Past it by one
  // and no further, because the stamp is also the row's place in Recent: stamped
  // "now", every boot that found a stub filed the worked example above the
  // exploration the reader had open and everything they had worked on since. A
  // repair is not somebody's work, so the row stays where their last visit left
  // it. (The open demo's branch above is an edit of what is on screen, and is
  // saved like one — at the top, where opening it had already put it.)
  return writeDoc({ ...doc, savedAt: doc.savedAt + 1, session }, { keepPlace: true }).ok
}
